import React, { Context, StrictMode } from 'react';
import { HelmetContext, Provider as HelmetProvider } from '@okapi/helmet-react';
import { RenderCallback } from '@okapi/core';
import { GlobalRouter, Router } from '@okapi/router-react';
import { RemoteProvider } from '@navi-toolbox/ui-components';
import { Store, StoreProvider } from 'store';
import { YandexMetricaScript, GoogleAnalyticsScript, USE_REMOTES } from 'common';
import { HrefLangMeta } from 'common/meta/href-lang-meta';
import { SeoRating } from 'common/components/seo-rating';
import { getLang } from 'utils';
import { PageMeta } from 'pages/page-meta';
import { App } from '../../app';
import { MetaRobots } from '@navi-toolbox/seo';
import { ReactReduxContextValue } from 'react-redux';

export type EntryProps = {
  router: GlobalRouter;
  remoteContext?: RenderCallback['remoteContext'];
  store: Store;
} & (ClientSideEntryProps | ServerSideEntryProps);

type ClientSideEntryProps = {
  helmetContext?: never;
};

type ServerSideEntryProps = {
  helmetContext?: HelmetContext;
};

export function createEntry({ router, helmetContext, remoteContext, store }: EntryProps) {
  const model = store?.model?.data;
  const seo = model?.seoProperties;
  const logoImage = model?.header?.logoImage ?? '';
  const analytics = model?.analytics;
  const path = router?.getPath();
  const lang = getLang();
  const context = helmetContext as HelmetContext & Context<ReactReduxContextValue>;
  const isRemotes = USE_REMOTES !== '0';

  return () => (
    <Router router={router}>
      <StrictMode>
        <HelmetProvider context={helmetContext}>
          <RemoteProvider active={isRemotes} context={remoteContext}>
            <StoreProvider value={store}>
              <App />
              <PageMeta seo={seo} path={path} host={store.host} lang={lang} logoImage={logoImage} />
              <MetaRobots content={seo?.metaRobotsContent || []} context={context} />
              <HrefLangMeta
                currentPage={`https://${store.host}${path}${path?.endsWith('/') ? '' : '/'}`}
                context={context}
              />
              <YandexMetricaScript id={analytics?.yaCode} />
              <GoogleAnalyticsScript id={analytics?.gtmCode} />
              <SeoRating />
            </StoreProvider>
          </RemoteProvider>
        </HelmetProvider>
      </StrictMode>
    </Router>
  );
}
